import { useDraggable, useDroppable } from '@dnd-kit/core';
import classNames from 'classnames';
import moment from 'moment';
import { FC } from 'react';
import { observer } from 'mobx-react-lite';
import { SCREENS } from 'src/constants/screens';
import { TBooleanInNumber } from 'src/types/main';
import { getTranslate } from 'src/utils/helpers';
import CustomTooltip, { TOOLTIP_DURATION, TOOLTIP_POSITION } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import { cashbackStore } from 'src/view/screens/MarketingScreen/screens/Cashback/store/cashbackStore';
import { TCashback } from 'src/view/screens/MarketingScreen/screens/Cashback/types/cashback';
import { Icons } from '~/assets/fonts/Icons/icons';
import { history } from '~/services/history';
import logger from '~/services/logger';
import Icon from '~/view/components/Controls/Icon/Icon';
import './style.scss';

interface IItem {
  text?: string | number;
  className: string;
  component?: React.ReactElement | null;
  onClick?: () => void;
}

interface IProps {
  id?: number;
  cashback?: TCashback;
  activeLimitReached: boolean;
}

const Item = (item: IItem) => {
  return (
    <li className={classNames('d-flex flex-row align-items-center', item.className)} onClick={item.onClick}>
      {item.component ? item.component : <p className="family-medium text-size-16 color-dark text-elipsis">{item.text}</p>}
    </li>
  );
};

const CashbackListItem: FC<IProps> = observer(({ id = 0, cashback, activeLimitReached }): JSX.Element | null => {
  const { attributes, isDragging, listeners, setNodeRef: draggableRef } = useDraggable({ id: id.toString(), data: { cashback } });
  const { isOver, setNodeRef: droppableRef } = useDroppable({ id: id.toString(), data: { cashback } });

  const isExpiredCashback = moment(`${cashback?.end_day}T${cashback?.end_time}`).isBefore(moment());
  const isDisabledStatus = isExpiredCashback || (!cashback?.state && activeLimitReached);

  const tooltipMsg = isExpiredCashback ? 'expired_cashback_tooltip' : activeLimitReached ? 'max_limit_of_active_cashbacks_reached_err' : undefined;

  const handleChangeStatus = (status: TBooleanInNumber, cb?: () => void) => {
    try {
      if (!cashback || !cashback.id) return null;
      cashbackStore.changeStatus(cashback.id, status, false, getTranslate(cashback.name), cb);
      cb?.();
    } catch (error: any) {
      logger.error({ error, place: 'CashbackListItem changeStatus' });
    }
  };

  const handleClick = () => {
    if (!cashback) return;
    history.push(SCREENS.CASHBACK_EDIT(cashback.id));
  };

  const wrapperClassName = classNames('cashback-item', {
    'drag-clone': isDragging,
    'drag-droppable': isOver,
    dragging: !id,
  });

  if (!cashback) return null;

  return (
    <ul ref={isDragging ? draggableRef : droppableRef} className={wrapperClassName}>
      <Item
        className="w-3"
        component={
          // eslint-disable-next-line react/jsx-props-no-spreading
          <button className={classNames('cursor-grab', { 'cursor-grabbing': !id })} {...listeners} {...attributes}>
            <Icon name={Icons.DragHandHorizontal} size={14} className="color-gray2" />
          </button>
        }
      />
      <Item className="pr-4 w-17" text={cashback.priority} onClick={handleClick} />
      <Item text={getTranslate(cashback.name)} className="pr-4 w-25" onClick={handleClick} />
      <Item text={cashback.percent} className="pr-4 w-15" onClick={handleClick} />
      <Item text={moment(cashback.start_day).format('DD/MM/YYYY')} className="pr-4 w-15" onClick={handleClick} />
      <Item text={moment(cashback.end_day).format('DD/MM/YYYY')} className="pr-4 w-15" onClick={handleClick} />
      <Item
        className="cashback-item__switcher w-10"
        component={
          <CustomTooltip position={TOOLTIP_POSITION.PORTAL} show={isDisabledStatus} message={tooltipMsg} duration={TOOLTIP_DURATION.EXTRA_LARGE}>
            <Switcher status={cashback.state} onChange={handleChangeStatus} hasCallback disabled={isDisabledStatus} />
            {/* <CustomTooltip
            triggerType={showStatusTooltip ? TOOLTIP_TRIGGERS.CLICK : TOOLTIP_TRIGGERS.NONE}
            position={TOOLTIP_POSITION.PORTAL}
            trigger={showStatusTooltip}
            message="expired_cashback_tooltip"
            duration={TOOLTIP_DURATION.LARGE}>
            <Switcher status={cashback.state} onChange={handleChangeStatus} hasCallback disabled={showStatusTooltip} /> */}
          </CustomTooltip>
        }
      />
    </ul>
  );
});

export default CashbackListItem;
